/**
 * Formate un prix avec la devise appropriée
 * @param {number|string} price - Le prix (en centimes pour EUR/USD/GBP, en unités complètes pour XAF/XOF)
 * @param {string} currency - La devise (EUR, USD, XAF, etc.)
 * @returns {string} - Le prix formaté avec symbole de devise
 */
export const formatPrice = (price, currency = 'EUR') => {
  if (!price || price === "0" || price === 0) return "Gratuit"
  
  const numericPrice = typeof price === 'string' ? parseInt(price, 10) : price
  
  // Mapping des devises vers leurs symboles
  const currencySymbols = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'XAF': 'FCFA',
    'XOF': 'FCFA',
    'MAD': 'MAD',
    'TND': 'TND'
  }
  
  const symbol = currencySymbols[currency] || currency
  
  // Pour le FCFA (XAF/XOF), le prix est déjà en unités complètes (pas en centimes)
  // On affiche directement sans diviser par 100
  if (currency === 'XAF' || currency === 'XOF') {
    return `${numericPrice.toLocaleString('fr-FR')} ${symbol}`
  }
  
  // Pour les autres devises (EUR, USD, GBP), le prix est en centimes
  // On divise par 100 pour obtenir les unités principales
  const priceInMainUnit = numericPrice / 100
  
  // Symbole après pour EUR, USD, GBP
  if (currency === 'EUR' || currency === 'USD' || currency === 'GBP') {
    return `${priceInMainUnit.toFixed(2)} ${symbol}`
  }
  
  // Symbole avant pour les autres devises
  return `${symbol} ${priceInMainUnit.toFixed(2)}`
}

/**
 * Formate une durée en secondes ou minutes vers un format lisible en heures
 * @param {number} duration - La durée en secondes (de l'API) ou minutes
 * @param {string} unit - 'seconds' ou 'minutes' (par défaut: 'seconds')
 * @returns {string} - La durée formatée en heures (ex: "2.5h", "74h", "336h")
 */
export const formatDuration = (duration, unit = 'seconds') => {
  if (!duration || duration === 0) return "0h"
  
  // Convertir en minutes si nécessaire
  const minutes = unit === 'seconds' ? duration / 60 : duration
  
  // Convertir en heures
  const hours = minutes / 60
  
  // Afficher en heures avec 1 décimale si nécessaire
  if (hours < 1) {
    return `${hours.toFixed(1)}h`
  }
  
  // Pour les durées >= 1h, afficher sans décimales si c'est un nombre entier
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`
}

/**
 * Formate une date en format local français
 * @param {string} dateString - La date au format ISO
 * @param {object} options - Options de formatage
 * @returns {string} - La date formatée
 */
export const formatDate = (dateString, options = {}) => {
  const defaultOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options
  }
  
  return new Date(dateString).toLocaleDateString('fr-FR', defaultOptions)
}

/**
 * Formate une date avec l'heure
 * @param {string} dateString - La date au format ISO
 * @returns {string} - La date et l'heure formatées
 */
export const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formate uniquement l'heure d'une date
 * @param {Date|string} date - La date (objet Date ou string ISO)
 * @returns {string} - L'heure formatée (HH:MM)
 */
export const formatTime = (date) => {
  const dateObj = date instanceof Date ? date : new Date(date)
  return dateObj.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}